import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import { DownOutlined } from '@ant-design/icons';
import { TRY_UNMERGE_ISSUE_SOURCES, tryUnmergeIssueSources, UnmergeIssueSourcesMode } from '@/actions/triage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { IssueEntity, IssueSource } from '@/types';
import { displayTextForIssueSourceType } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { selectAppStoreType, selectAppTimeFormatSetting, selectEffectiveTimezone } from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import { iconForIssueSource } from './triageHelpers';

const sourceOptionText = (source: IssueSource): string =>
    source.displayText || displayTextForIssueSourceType(source.type);

interface UnmergeSourceModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    issue: IssueEntity;
}

export const UnmergeSourceModal: React.FC<UnmergeSourceModalProps> = ({ open, onClose, orgId, issue }) => {
    const dispatch = useDispatch();
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, issue.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, issue.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, issue.appId));
    const [selectedSourceIds, setSelectedSourceIds] = React.useState<string[]>([]);
    const [submittedMode, setSubmittedMode] = React.useState<UnmergeIssueSourcesMode | null>(null);

    const isSaving = useIsLoading(TRY_UNMERGE_ISSUE_SOURCES, issue.id);
    const saveError = useError(TRY_UNMERGE_ISSUE_SOURCES, issue.id);
    const saveSucceeded = useUpdated(TRY_UNMERGE_ISSUE_SOURCES, issue.id);

    React.useEffect(() => {
        if (saveSucceeded) {
            onClose();
        }
    }, [saveSucceeded, onClose]);

    React.useEffect(() => {
        if (!open) {
            setSelectedSourceIds([]);
            setSubmittedMode(null);
        }
    }, [open]);

    const onSubmit = (mode: UnmergeIssueSourcesMode) => {
        setSubmittedMode(mode);
        dispatch(
            tryUnmergeIssueSources({
                orgId,
                appId: issue.appId,
                issueId: issue.id,
                sourceIds: selectedSourceIds,
                mode,
            }),
        );
    };

    const sources = issue.sources ?? [];
    const options = sources.map((source) => {
        const text = sourceOptionText(source);
        const dateLabel = formatDateWithTimezone(
            source.createdAt,
            'shortWithTimezone',
            effectiveTimezone,
            use24HourTimeFormat,
        );
        return {
            value: source.id,
            searchText: text,
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
                    <span style={{ flexShrink: 0, lineHeight: 0 }}>{iconForIssueSource(source, appStoreType)}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
                    {dateLabel && (
                        <span style={{ marginLeft: 'auto', flexShrink: 0, color: antGrey.grey7, fontSize: 12 }}>
                            {dateLabel}
                        </span>
                    )}
                </span>
            ),
        };
    });

    const selectedCount = selectedSourceIds.length;
    return (
        <Modal
            title="Unmerge source"
            open={open}
            onCancel={onClose}
            destroyOnClose
            width={560}
            footer={
                <>
                    <Button onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        disabled={selectedCount === 0 || (isSaving && submittedMode !== 'single')}
                        loading={isSaving && submittedMode === 'single'}
                        onClick={() => onSubmit('single')}
                    >
                        {selectedSourceIds.length > 1 ? 'Split all into one new issue' : 'Split into new issue'}
                    </Button>
                    {selectedCount > 1 && (
                        <Button
                            disabled={isSaving && submittedMode !== 'perSource'}
                            loading={isSaving && submittedMode === 'perSource'}
                            onClick={() => onSubmit('perSource')}
                        >
                            {`Split into ${selectedCount} new issues`}
                        </Button>
                    )}
                </>
            }
        >
            {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Select one or more sources to unmerge from this issue. You can choose to split selected sources out into
                individual issues, or all into one issue.
            </Text>
            <Select
                mode="multiple"
                allowClear
                value={selectedSourceIds}
                onChange={setSelectedSourceIds}
                options={options}
                suffixIcon={<DownOutlined className="ant-select-suffix" />}
                maxCount={Math.max(sources.length - 1, 0)}
                filterOption={(input, option) => (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())}
                placeholder="Select sources to unmerge"
                style={{ width: '100%' }}
            />
        </Modal>
    );
};
